import { Discount, RegistrationPricingLine } from '../../types/orders.type';

type OrderLike = {
  id: number;
  parent_id: number;
  course_id: number;
};

type RegistrationLike = {
  child_id: number;
  price?: number | null;
  full_price?: number | null;
  child?: {
    first_name?: string;
    last_name?: string;
    full_name?: string;
  };
};

export type OrderWithRegistrations = OrderLike & {
  registrations?: RegistrationLike[];
  total?: number | null;
};

const roundMoney = (amount: number): number => Math.round(amount * 100) / 100;

/** True when the admin assigned this discount to a specific child. */
export const isChildScopedDiscount = (discount: Pick<Discount, 'child_id'>): boolean =>
  discount.child_id != null && Number(discount.child_id) > 0;

const getChildDisplayName = (discount: Discount): string => {
  const child = discount.child;
  if (!child) {
    return '';
  }
  return child.full_name?.trim() || `${child.first_name ?? ''} ${child.last_name ?? ''}`.trim();
};

const getRegistrationChildName = (registration: RegistrationLike): string => {
  const child = registration.child;
  if (child) {
    const name =
      child.full_name?.trim() || `${child.first_name ?? ''} ${child.last_name ?? ''}`.trim();
    if (name) {
      return name;
    }
  }
  return `Student`;
};

const getRegistrationPrice = (
  registration: RegistrationLike,
  subtotal: number,
  registrationCount: number
): number => {
  const price = Number(registration.price ?? 0);
  if (price > 0) {
    return price;
  }
  return registrationCount > 0 ? subtotal / registrationCount : 0;
};

const getScholarshipBadgeLabel = (
  discount: Discount,
  formatType: (value: string) => string
): string => {
  const percent = discount.scholarship?.percent_discount ?? 0;
  const type = discount.scholarship?.scholarship_type?.trim();
  if (type) {
    return `${formatType(type)} (${percent}% off)`;
  }
  return `${percent}% scholarship`;
};

export const formatDiscountOptionLabel = (
  discount: Discount,
  formatType: (value: string) => string
): string => {
  const type = formatType(discount.scholarship?.scholarship_type ?? '');
  const percent = discount.scholarship?.percent_discount ?? 0;
  const session = discount.course?.title ?? 'Unknown session';
  let label = `${type} (${percent}% off) — ${session}`;

  if (isChildScopedDiscount(discount)) {
    const childName = getChildDisplayName(discount);
    if (childName) {
      label += ` · ${childName}`;
    }
  }

  return label;
};

export const discountAppliesToOrder = (
  discount: Discount,
  order: OrderLike,
  childIds: number[]
): boolean =>
  !!discount.user_id &&
  discount.user_id === order.parent_id &&
  (discount.order_id ? discount.order_id === order.id : true) &&
  (discount.course_id ? discount.course_id === order.course_id : true) &&
  (isChildScopedDiscount(discount) ? childIds.includes(Number(discount.child_id)) : true);

export const hasChildScopedDiscountForOrder = (
  order: OrderWithRegistrations,
  discounts: Discount[] | undefined
): boolean => {
  const childIds = order.registrations?.map((registration) => registration.child_id) ?? [];
  return (
    discounts?.some(
      (discount) => isChildScopedDiscount(discount) && discountAppliesToOrder(discount, order, childIds)
    ) ?? false
  );
};

/** Prefer the sum of registration prices; fall back to order.total. */
export const getOrderChargeSubtotal = (order: OrderWithRegistrations): number => {
  const fromRegistrations =
    order.registrations?.reduce((acc, registration) => acc + Number(registration.price ?? 0), 0) ??
    0;

  if (fromRegistrations > 0) {
    return fromRegistrations;
  }

  return Number(order.total ?? 0);
};

/**
 * Returns the fractional discount (0–1) to apply to an order total.
 * Order-wide discounts apply the full scholarship % to the entire order.
 * Child-scoped discounts apply only to that child's share of the order.
 */
export const getDiscountRateForOrder = (
  discount: Discount,
  registrationCount: number,
  childIds: number[]
): number => {
  const percent = discount.scholarship?.percent_discount ?? 0;
  if (!percent) {
    return 0;
  }

  if (!isChildScopedDiscount(discount)) {
    return percent / 100;
  }

  if (!childIds.includes(Number(discount.child_id)) || registrationCount <= 0) {
    return 0;
  }

  return percent / registrationCount / 100;
};

export const sumApplicableDiscountRates = (
  discounts: Discount[] | undefined,
  order: OrderLike,
  childIds: number[],
  registrationCount: number
): number => {
  const rate =
    discounts
      ?.filter((d) => discountAppliesToOrder(d, order, childIds))
      .reduce((acc, d) => acc + getDiscountRateForOrder(d, registrationCount, childIds), 0) ?? 0;

  return Math.min(rate, 1);
};

const distributeRoundedTotals = (amounts: number[], targetTotal: number): number[] => {
  if (amounts.length === 0) {
    return [];
  }

  const rounded = amounts.map((amount) => roundMoney(amount));
  const diff = roundMoney(targetTotal - rounded.reduce((sum, amount) => sum + amount, 0));

  if (diff !== 0) {
    rounded[rounded.length - 1] = roundMoney(rounded[rounded.length - 1] + diff);
  }

  return rounded;
};

/**
 * Per-student pricing for checkout summary. Line dues sum to getDiscountedOrderTotal.
 */
export const getRegistrationPricingLines = (
  order: OrderWithRegistrations,
  discounts: Discount[] | undefined,
  promoDiscountRate = 0,
  formatType: (value: string) => string = (value) => value
): RegistrationPricingLine[] => {
  const registrations = order.registrations ?? [];
  if (registrations.length === 0) {
    return [];
  }

  const childIds = registrations.map((registration) => registration.child_id);
  const subtotal = getOrderChargeSubtotal(order);
  const registrationCount = registrations.length;

  type DraftLine = {
    childId: number;
    childName: string;
    price: number;
    fullPrice: number;
    afterChildScoped: number;
    badges: string[];
  };

  const draftLines: DraftLine[] = registrations.map((registration) => {
    const price = getRegistrationPrice(registration, subtotal, registrationCount);
    const fullPrice = Number(registration.full_price ?? 0) || price;
    let childScopedReduction = 0;
    const badges: string[] = [];

    if (price !== fullPrice) {
      badges.push('Prorated');
    }

    for (const discount of discounts ?? []) {
      if (!discountAppliesToOrder(discount, order, childIds)) {
        continue;
      }

      const percent = discount.scholarship?.percent_discount ?? 0;
      if (!percent) {
        continue;
      }

      if (
        isChildScopedDiscount(discount) &&
        Number(registration.child_id) === Number(discount.child_id)
      ) {
        childScopedReduction += price * (percent / 100);
        badges.push(getScholarshipBadgeLabel(discount, formatType));
      }
    }

    childScopedReduction = Math.min(childScopedReduction, price);

    return {
      childId: registration.child_id,
      childName: getRegistrationChildName(registration),
      price,
      fullPrice,
      afterChildScoped: price - childScopedReduction,
      badges
    };
  });

  let orderWideRate = 0;
  for (const discount of discounts ?? []) {
    if (!discountAppliesToOrder(discount, order, childIds)) {
      continue;
    }

    const percent = discount.scholarship?.percent_discount ?? 0;
    if (!percent || isChildScopedDiscount(discount)) {
      continue;
    }

    orderWideRate += percent / 100;
    const badge = getScholarshipBadgeLabel(discount, formatType);
    for (const line of draftLines) {
      if (!line.badges.includes(badge)) {
        line.badges.push(badge);
      }
    }
  }

  orderWideRate = Math.min(orderWideRate, 1);

  const afterChildScopedTotal = draftLines.reduce((sum, line) => sum + line.afterChildScoped, 0);
  const scholarshipTotal = roundMoney(afterChildScopedTotal * (1 - orderWideRate));
  const promoTotal = roundMoney(subtotal * (1 - promoDiscountRate));
  const usePromo = promoDiscountRate > 0 && promoTotal < scholarshipTotal;

  const rawDues = draftLines.map((line) => {
    if (usePromo) {
      return subtotal > 0 ? promoTotal * (line.price / subtotal) : 0;
    }
    return line.afterChildScoped * (1 - orderWideRate);
  });

  const targetTotal = usePromo ? promoTotal : scholarshipTotal;
  const dues = distributeRoundedTotals(rawDues, targetTotal);

  return draftLines.map((line, index) => {
    const due = dues[index] ?? line.price;
    const badges = [...line.badges];

    if (usePromo && promoDiscountRate > 0) {
      badges.push(`${Math.round(promoDiscountRate * 100)}% promo`);
    }

    return {
      childId: line.childId,
      childName: line.childName,
      price: line.price,
      fullPrice: line.fullPrice,
      due,
      badges
    };
  });
};

/**
 * Dollar amount due after scholarships and promo.
 * Child-scoped discounts reduce only that child's registration line; order-wide
 * discounts apply to the remaining balance.
 */
export const getDiscountedOrderTotal = (
  order: OrderWithRegistrations,
  discounts: Discount[] | undefined,
  promoDiscountRate = 0,
  formatType?: (value: string) => string
): number => {
  const lines = getRegistrationPricingLines(order, discounts, promoDiscountRate, formatType);
  if (lines.length === 0) {
    return roundMoney(getOrderChargeSubtotal(order) * (1 - promoDiscountRate));
  }

  return roundMoney(lines.reduce((sum, line) => sum + line.due, 0));
};
